import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import {
  ArrowLeftIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import adminService from '../../services/admin.service';
import { AnalysisDetail as AnalysisDetailType } from '../../types/analysis.types';
import { AdminUserDetail, AnalysisCostDetail } from '../../types/admin.types';
import ConfirmationDialog from '../../components/shared/ConfirmationDialog';
import PipelineStackBadge from '../../components/admin/PipelineStackBadge';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDurationSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  queued: { className: 'bg-yellow-100 text-yellow-800', label: 'Queued' },
  processing: { className: 'bg-blue-100 text-blue-800', label: 'Processing' },
  completed: { className: 'bg-green-100 text-green-800', label: 'Completed' },
  failed: { className: 'bg-red-100 text-red-800', label: 'Failed' },
  uploading: { className: 'bg-gray-100 text-gray-800', label: 'Uploading' },
};

const AdminUserAnalysisDetail: React.FC = () => {
  const { id: userId, analysisId } = useParams<{ id: string; analysisId: string }>();
  const [analysis, setAnalysis] = useState<AnalysisDetailType | null>(null);
  const [userDetail, setUserDetail] = useState<AdminUserDetail | null>(null);
  const [costData, setCostData] = useState<AnalysisCostDetail | null>(null);
  // Tracks the cost fetch separately from the main analysis fetch so the
  // Cost Breakdown card can show "loading…" then "no data" instead of
  // briefly disappearing while the deferred cost fetch is still in flight.
  const [costLoading, setCostLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [retryingAnalysis, setRetryingAnalysis] = useState(false);
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);

  useEffect(() => {
    if (!userId || !analysisId) return;

    const fetchData = async () => {
      try {
        const [analysisData, userData] = await Promise.all([
          adminService.getUserAnalysis(userId, analysisId),
          adminService.getUserDetail(userId),
        ]);
        setAnalysis(analysisData);
        setUserDetail(userData);

        // Fetch cost data separately — don't block on failure. The endpoint
        // returns 404 for analyses written before cost-tracking shipped (or
        // if the decoupled record_analysis_cost task hasn't run yet); we
        // surface that as an empty-state card rather than just hiding the
        // section, so admins can tell "no data recorded" from "loading".
        adminService.getAnalysisCost(userId, analysisId)
          .then((cost) => {
            if (cost.total_cost !== null) setCostData(cost);
          })
          .catch(() => { /* No cost record (404 or pre-cost-tracking) */ })
          .finally(() => setCostLoading(false));
      } catch {
        setError('Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, analysisId]);

  const handleRetryConfirm = async () => {
    if (!analysisId) return;
    setRetryingAnalysis(true);
    try {
      await adminService.retryAnalysis(analysisId);
      toast.success('Analysis retry queued');
      if (userId && analysisId) {
        const updated = await adminService.getUserAnalysis(userId, analysisId);
        setAnalysis(updated);
      }
    } catch {
      toast.error('Failed to retry analysis');
    } finally {
      setRetryingAnalysis(false);
      setShowRetryConfirm(false);
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-64 bg-white rounded-xl shadow" />
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="space-y-4">
        <Link
          to={`/admin/users/${userId}/analyses`}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to analyses
        </Link>
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error || 'Analysis not found'}</p>
        </div>
      </div>
    );
  }

  const badge = STATUS_BADGE[analysis.status] || {
    className: 'bg-gray-100 text-gray-800',
    label: analysis.status,
  };
  const hasResults = analysis.status === 'completed' && analysis.results;

  // Compliance helpers
  const getComplianceScore = () => {
    if (!analysis.results?.compliance) return { passed: 0, total: 5 };
    const c = analysis.results.compliance;
    let passed = 0;
    if (!c.identity_verification.flagged) passed++;
    if (c.mandatory_disclosures.read) passed++;
    if (!c.purpose_declaration.flagged) passed++;
    if (!c.prohibited_phrases.used) passed++;
    if (!c.sensitive_data.shared) passed++;
    return { passed, total: 5 };
  };

  const complianceScore = getComplianceScore();

  return (
    <div className="space-y-6">
      {/* Context Banner */}
      <div className="flex items-center gap-4">
        <Link
          to={`/admin/users/${userId}/analyses`}
          className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {userDetail?.email ? `${userDetail.email}'s Call` : 'Call Analysis'}
          </h1>
          <p className="text-sm text-gray-500">
            Analysis ID: {String(analysis.id).slice(0, 8)}... | Status:{' '}
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </p>
        </div>
      </div>

      {/* Error Message - Prominent for failed analyses */}
      {analysis.status === 'failed' && (
        <div className="rounded-xl bg-red-50 border-2 border-red-200 p-6">
          <div className="flex items-start gap-3">
            <XCircleIcon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800">Analysis Failed</h3>
              <p className="mt-1 text-sm text-red-700">
                {analysis.metadata.error_message || 'No error message available'}
              </p>
              <button
                onClick={() => setShowRetryConfirm(true)}
                disabled={retryingAnalysis}
                className="mt-3 inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-300 hover:bg-red-50 disabled:opacity-50"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {retryingAnalysis ? 'Retrying...' : 'Retry Analysis'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Score Summary Cards */}
      {hasResults && analysis.results && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Compliance */}
          <div className={classNames(
            'rounded-xl p-5 border-2',
            complianceScore.passed < complianceScore.total
              ? 'bg-red-50 border-red-200'
              : 'bg-green-50 border-green-200',
          )}>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheckIcon className={classNames(
                'h-5 w-5',
                complianceScore.passed < complianceScore.total ? 'text-red-600' : 'text-green-600',
              )} />
              <span className="text-sm font-medium text-gray-700">Compliance</span>
            </div>
            <span className={classNames(
              'text-3xl font-bold',
              complianceScore.passed < complianceScore.total ? 'text-red-700' : 'text-green-700',
            )}>
              {complianceScore.passed}/{complianceScore.total}
            </span>
          </div>

          {/* Quality */}
          {analysis.results.quality && (
            <div className={classNames(
              'rounded-xl p-5 border-2',
              analysis.results.quality.sentiment_analysis?.flagged_drop
                ? 'bg-amber-50 border-amber-200'
                : 'bg-green-50 border-green-200',
            )}>
              <div className="flex items-center gap-2 mb-2">
                <SparklesIcon className="h-5 w-5 text-amber-600" />
                <span className="text-sm font-medium text-gray-700">Quality</span>
              </div>
              <div className="text-sm space-y-1">
                {analysis.results.quality.sentiment_analysis?.flagged_drop && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                    Sentiment Drop
                  </span>
                )}
                {analysis.results.quality.greeting?.flagged_missing && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ml-1">
                    Missing Greeting
                  </span>
                )}
                {!analysis.results.quality.sentiment_analysis?.flagged_drop &&
                 !analysis.results.quality.greeting?.flagged_missing && (
                  <span className="text-green-700 font-medium">All Good</span>
                )}
              </div>
            </div>
          )}

          {/* Coaching */}
          {analysis.results.coaching && (
            <div className="rounded-xl p-5 border-2 bg-indigo-50 border-indigo-200">
              <div className="flex items-center gap-2 mb-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700">Coaching</span>
              </div>
              <div className="text-sm space-y-1">
                {analysis.results.coaching.issues && analysis.results.coaching.issues.length > 0 && (
                  <div className="text-red-600 font-medium">
                    {analysis.results.coaching.issues.length} Issues
                  </div>
                )}
                {analysis.results.coaching.strengths && analysis.results.coaching.strengths.length > 0 && (
                  <div className="text-green-600 font-medium">
                    {analysis.results.coaching.strengths.length} Strengths
                  </div>
                )}
                {analysis.results.coaching.growth_opportunities && analysis.results.coaching.growth_opportunities.length > 0 && (
                  <div className="text-amber-600 font-medium">
                    {analysis.results.coaching.growth_opportunities.length} Growth Opportunities
                  </div>
                )}
                {analysis.results.coaching.customer_effort_score !== undefined && (
                  <div className="text-gray-600">
                    Effort Score: {analysis.results.coaching.customer_effort_score}/5
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compliance Details */}
      {hasResults && analysis.results?.compliance && (
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance Details</h3>
          <div className="space-y-3">
            {[
              {
                label: 'Identity Verification',
                passed: !analysis.results.compliance.identity_verification.flagged,
                detail: analysis.results.compliance.identity_verification.flagged
                  ? 'Identity not verified'
                  : 'Identity verified',
              },
              {
                label: 'Mandatory Disclosures',
                passed: analysis.results.compliance.mandatory_disclosures.read,
                detail: !analysis.results.compliance.mandatory_disclosures.read
                  ? `${analysis.results.compliance.mandatory_disclosures.missing_disclosures.length} disclosures missing`
                  : 'All disclosures read',
              },
              {
                label: 'Purpose Declaration',
                passed: !analysis.results.compliance.purpose_declaration.flagged,
                detail: analysis.results.compliance.purpose_declaration.flagged
                  ? 'Purpose not declared'
                  : 'Purpose declared',
              },
              {
                label: 'Prohibited Phrases',
                passed: !analysis.results.compliance.prohibited_phrases.used,
                detail: analysis.results.compliance.prohibited_phrases.used
                  ? `${analysis.results.compliance.prohibited_phrases.instances.length} instances detected`
                  : 'None detected',
              },
              {
                label: 'Sensitive Data',
                passed: !analysis.results.compliance.sensitive_data.shared,
                detail: analysis.results.compliance.sensitive_data.shared
                  ? `${analysis.results.compliance.sensitive_data.instances.length} instances detected`
                  : 'No sensitive data shared',
              },
            ].map((check) => (
              <div key={check.label} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2">
                  {check.passed ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                  <span className="text-sm font-medium text-gray-900">{check.label}</span>
                </div>
                <span className={`text-sm ${check.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {check.detail}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coaching Details */}
      {hasResults && analysis.results?.coaching && (
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Coaching Insights</h3>
          <div className="space-y-4">
            {/* Issues */}
            {analysis.results.coaching.issues && analysis.results.coaching.issues.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4" /> Issues
                </h4>
                <div className="space-y-2">
                  {analysis.results.coaching.issues.map((item, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-red-50 border border-red-200">
                      <p className="text-sm font-medium text-gray-800">{item.category}</p>
                      {item.timestamp != null && (
                        <p className="text-xs text-red-600 mt-1">at {formatTimestamp(item.timestamp)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths */}
            {analysis.results.coaching.strengths && analysis.results.coaching.strengths.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1">
                  <CheckCircleIcon className="h-4 w-4" /> Strengths
                </h4>
                <div className="space-y-2">
                  {analysis.results.coaching.strengths.map((item, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-green-50 border border-green-200">
                      <p className="text-sm font-medium text-gray-800">{item.category}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Growth Opportunities */}
            {analysis.results.coaching.growth_opportunities && analysis.results.coaching.growth_opportunities.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1">
                  <SparklesIcon className="h-4 w-4" /> Growth Opportunities
                </h4>
                <div className="space-y-2">
                  {analysis.results.coaching.growth_opportunities.map((item, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <p className="text-sm font-medium text-gray-800">{item.category}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy recommendations fallback */}
            {analysis.results.coaching.recommendations && analysis.results.coaching.recommendations.length > 0 &&
             !analysis.results.coaching.issues?.length && (
              <div className="space-y-2">
                {analysis.results.coaching.recommendations
                  .sort((a, b) => {
                    const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
                    return (order[a.priority] ?? 3) - (order[b.priority] ?? 3);
                  })
                  .map((rec, idx) => (
                    <div
                      key={idx}
                      className={classNames(
                        'p-3 rounded-lg border',
                        rec.priority === 'high' ? 'bg-red-50 border-red-200' :
                        rec.priority === 'medium' ? 'bg-amber-50 border-amber-200' :
                        'bg-blue-50 border-blue-200',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className={classNames(
                          'text-xs px-2 py-0.5 rounded font-bold text-white',
                          rec.priority === 'high' ? 'bg-red-500' :
                          rec.priority === 'medium' ? 'bg-amber-500' :
                          'bg-blue-500',
                        )}>
                          {rec.priority.toUpperCase()}
                        </span>
                        <p className="text-sm font-medium text-gray-800">{rec.category}</p>
                      </div>
                      {rec.description && (
                        <p className="text-xs text-gray-600 mt-1 ml-14">{rec.description}</p>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transcript */}
      {analysis.transcript && (
        <div className="rounded-xl bg-white shadow overflow-hidden">
          <button
            onClick={() => setIsTranscriptExpanded(!isTranscriptExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900">Transcript</h3>
            </div>
            <div className="flex items-center gap-3">
              {analysis.audio.url && (
                <a
                  href={analysis.audio.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100"
                >
                  <PlayIcon className="h-4 w-4 mr-1" />
                  Play Audio
                </a>
              )}
              {isTranscriptExpanded ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {isTranscriptExpanded && (
            <div className="px-6 pb-6">
              {analysis.transcript.segments && analysis.transcript.segments.length > 0 ? (
                <div className="space-y-4">
                  {analysis.transcript.segments.map((segment, idx) => {
                    const agentSpeaker = analysis.transcript?.agent_speaker || 'A';
                    const isAgent = segment.speaker === agentSpeaker;
                    const speakerLabel = isAgent ? 'Agent' : segment.speaker ? 'Customer' : 'Unknown';
                    const avatarLetter = isAgent ? 'A' : 'C';

                    return (
                      <div key={idx} className="flex gap-3">
                        <div className={classNames(
                          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                          isAgent ? 'bg-indigo-100' : 'bg-amber-100',
                        )}>
                          <span className={classNames(
                            'text-xs font-bold',
                            isAgent ? 'text-indigo-600' : 'text-amber-600',
                          )}>
                            {avatarLetter}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={classNames(
                              'text-sm font-semibold',
                              isAgent ? 'text-indigo-600' : 'text-amber-600',
                            )}>
                              {speakerLabel}
                            </span>
                            {segment.start !== undefined && (
                              <span className="text-xs text-gray-400">
                                {formatTimestamp(segment.start)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                            {segment.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {analysis.transcript.text}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Call Information - moved below transcript to match user-facing page flow */}
      <div className="rounded-xl bg-white shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Call Information</h3>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase">Agent ID</dt>
            <dd className="mt-1 text-sm font-medium text-gray-900">{analysis.call.agent_id || 'N/A'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase">Contact ID</dt>
            <dd className="mt-1 text-sm font-medium text-gray-900">{analysis.call.contact_id || 'N/A'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase">Duration</dt>
            <dd className="mt-1 text-sm font-medium text-gray-900">
              {formatDuration(analysis.call.duration_ms)}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase">Language</dt>
            <dd className="mt-1 text-sm font-medium text-gray-900">
              {analysis.transcript?.language || 'N/A'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase">Created</dt>
            <dd className="mt-1 text-sm font-medium text-gray-900">
              {new Date(analysis.created * 1000).toLocaleString()}
            </dd>
          </div>
          {analysis.metadata.completed_at && (
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Completed</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900">
                {new Date(analysis.metadata.completed_at).toLocaleString()}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Cost Breakdown — always rendered after the main analysis loads,
          so seeded / pre-cost-tracking analyses get a clear empty state
          instead of just an absent section. */}
      <div className="rounded-xl bg-white shadow p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-medium text-gray-900">Cost Breakdown</h3>
            {costData?.pipeline_stack && (
              <PipelineStackBadge stack={costData.pipeline_stack} />
            )}
          </div>
          {costData && (
            <span className="text-lg font-semibold text-gray-900">
              ${parseFloat(costData.total_cost!).toFixed(4)}
            </span>
          )}
        </div>

        {costLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-full bg-gray-100 rounded" />
            <div className="h-4 w-3/4 bg-gray-100 rounded" />
            <div className="h-4 w-1/2 bg-gray-100 rounded" />
          </div>
        ) : !costData ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center">
            <p className="text-sm font-medium text-gray-700">
              No cost data recorded for this analysis.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              This is expected for seeded calls or analyses that ran before
              cost telemetry shipped. New analyses get a per-call bill of
              materials automatically.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Per-call summary: duration, on-disk size, cost-per-minute. All
                three are sourced upstream — duration from the transcription
                provider's response, size from a live head_object, cost-per-min
                from the recorded total. No client-side recomputation. */}
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {costData.audio_duration_seconds && (
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500">Call duration</div>
                  <div className="text-sm font-medium text-gray-900 tabular-nums">
                    {formatDurationSeconds(parseFloat(costData.audio_duration_seconds))}
                  </div>
                </div>
              )}
              {costData.audio_size_bytes != null && (
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500">Call size on disk</div>
                  <div className="text-sm font-medium text-gray-900 tabular-nums">
                    {formatBytes(costData.audio_size_bytes)}
                  </div>
                </div>
              )}
              {costData.cost_per_minute && (
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500">Cost per minute</div>
                  <div className="text-sm font-medium text-gray-900 tabular-nums">
                    ${parseFloat(costData.cost_per_minute).toFixed(6)}
                  </div>
                </div>
              )}
            </div>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Capability</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Provider</th>
                  <th className="text-right py-2 pr-4 font-medium text-gray-500">Usage</th>
                  <th className="text-right py-2 font-medium text-gray-500">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {costData.api_calls.map((call, idx) => {
                  const totalTokens = (call.input_tokens || 0) + (call.output_tokens || 0);
                  const hasTokens = call.input_tokens != null || call.output_tokens != null;
                  // Render the most relevant per-call usage metric: tokens
                  // for LLM calls, GPU seconds for self-hosted compute,
                  // duration for managed transcription, request counts and
                  // size for S3 storage.
                  let usageDisplay: string;
                  if (hasTokens) {
                    usageDisplay = `${totalTokens.toLocaleString()} tok`;
                  } else if (call.gpu_seconds != null) {
                    usageDisplay = `${call.gpu_seconds.toFixed(2)}s GPU`;
                  } else if (call.size_bytes != null) {
                    const reqs = (call.get_count || 0) + (call.put_count || 0);
                    usageDisplay = `${formatBytes(call.size_bytes)} · ${reqs} req`;
                  } else if (call.duration_seconds != null) {
                    usageDisplay = `${call.duration_seconds.toFixed(0)}s audio`;
                  } else {
                    usageDisplay = '\u2014';
                  }
                  return (
                    <tr key={idx}>
                      <td className="py-2 pr-4 text-gray-900">
                        {call.capability.replace(/_/g, ' ')}
                        {call.detectors && call.detectors.length > 0 && (
                          <span className="block text-xs text-gray-400" title={call.detectors.join(', ')}>
                            {call.detectors.length} detectors
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-gray-600">
                        {call.provider}
                        <span className="text-gray-400 ml-1 text-xs">/ {call.model}</span>
                      </td>
                      <td className="py-2 pr-4 text-right text-gray-600 tabular-nums">
                        {usageDisplay}
                      </td>
                      <td className="py-2 text-right text-gray-900 tabular-nums">
                        ${parseFloat(call.cost).toFixed(6)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-gray-200">
                <tr>
                  <td colSpan={3} className="py-2 pr-4 text-right font-medium text-gray-700">Total</td>
                  <td className="py-2 text-right font-semibold text-gray-900 tabular-nums">
                    ${parseFloat(costData.total_cost!).toFixed(6)}
                  </td>
                </tr>
                {costData.cost_per_minute && (
                  <tr>
                    <td colSpan={3} className="py-1 pr-4 text-right text-xs text-gray-500">Cost/min</td>
                    <td className="py-1 text-right text-xs text-gray-500 tabular-nums">
                      ${parseFloat(costData.cost_per_minute).toFixed(6)}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Retry Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showRetryConfirm}
        onClose={() => setShowRetryConfirm(false)}
        onConfirm={handleRetryConfirm}
        title="Retry Analysis"
        message="Retry this analysis? It will be re-queued for processing."
        confirmLabel="Retry"
        loading={retryingAnalysis}
      />
    </div>
  );
};

export default AdminUserAnalysisDetail;
